import { type FC, useEffect, useRef } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useAssetEstimate } from "@/shared/hooks/useAssetEstimate";
import { EstimateLoadingAnimation } from "@/shared/components/EstateItems/EstimateLoadingAnimation";
import { sentryUtils } from "@/shared/utils/sentry";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

export const VehicleEstimator: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, updateItem } = useEstateItems(estateId);
  const estimateMutation = useAssetEstimate();
  const hasRun = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const assetsRef = useRef(assets);
  assetsRef.current = assets;

  const done = () => {
    goToScreen("vehicles_review_step", {
      ...answersRef.current,
      vehicle_estimate_done: "yes",
    });
  };

  useEffect(() => {
    if (hasRun.current || !estateId) return;

    const vehicleId =
      typeof answersRef.current["last_vehicle_id"] === "string"
        ? parseInt(answersRef.current["last_vehicle_id"] as string)
        : undefined;

    const vehicle = vehicleId ? assets.find((a) => a.id === vehicleId) : undefined;

    if (vehicleId && !vehicle) return;

    hasRun.current = true;

    if (!vehicle) {
      done();
      return;
    }

    const meta = (vehicle.metadata ?? {}) as Record<string, unknown>;
    const year = meta.year as number | undefined;
    const make = (meta.make as string) || "";
    const model = (meta.model as string) || "";

    if (!year || !make) {
      done();
      return;
    }

    estimateMutation
      .mutateAsync({
        estateId,
        assetType: "vehicle",
        data: { year, make, model, condition: "good" as const },
      })
      .then(async (estimate) => {
        if (estimate.estimatedValue > 0) {
          try {
            await updateItem(vehicle.id, {
              balance: estimate.estimatedValue,
              metadata: { ...meta, value_source: "smart_estimator" },
            });
          } catch (err) {
            sentryUtils.captureError(err, "Vehicle estimate update failed", {
              userId: user?.user_id,
              estateId: person?.estate_id,
            });
          }
        }
      })
      .catch((err) => {
        sentryUtils.captureError(err, "Vehicle estimate failed", {
          userId: user?.user_id,
          estateId: person?.estate_id,
        });
      })
      .finally(() => {
        done();
      });
  }, [estateId, assets, estimateMutation, updateItem, user, person, goToScreen]);

  return (
    <div className="mx-auto flex w-full max-w-[600px] flex-col">
      <EstimateLoadingAnimation assetType="vehicle" />
    </div>
  );
};
